import { HtmlLabel } from '@visx/annotation'
import { curveBasis } from '@visx/curve'
import { Group } from '@visx/group'
import { scaleLinear } from '@visx/scale'
import { Area } from '@visx/shape'
import classNames from 'classnames'
import { OpenInWindow } from 'iconoir-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import TrendLabel from '../TrendLabel'
import { Info } from 'app/components/Info'
import { hueOutline, hueTint } from 'lib/chartColors'
import type { ChartHue } from 'app/types/chartColors'

/** Stage fills step through one hue. */
const FUNNEL_STAGE_ALPHAS = [0.32, 0.46, 0.6, 0.74]
const FUNNEL_HALO_ALPHA = 0.12

const x = (d: { index: number; value: number }): number => d.index
const y = (d: { index: number; value: number }): number => Math.max(d.value, 0.25)
const spaceForText = 170

function range(n: number): number[] {
  return Array.from(Array(n).keys())
}

type DataPoint = { index: number; value: number }

function interpolatePoints(current: DataPoint, next?: DataPoint): DataPoint | DataPoint[] {
  if (!next) return current
  const xStep = 0.25
  const yStep = Math.abs(y(next) - y(current)) * 0.03
  const yMid1 = Math.abs(y(current) - yStep)
  const yMid2 = Math.abs(y(next) + yStep)
  const xMid1 = Math.abs(x(current) + xStep)
  const xMid2 = Math.abs(x(next) - xStep)
  return [current, { index: xMid1, value: yMid1 }, { index: xMid2, value: yMid2 }]
}

function interpolateData(data: DataPoint[]): DataPoint[] {
  return data?.map((d, i) => interpolatePoints(d, data[i + 1])).flat() || []
}

function mapDataToSections(data: FunnelSection[]): DataPoint[] {
  const secs = [...data]
  const lastItem = data.at(-1)
  if (lastItem) secs.push(lastItem)
  const firstValue = secs[0]?.value || 0
  return secs.map((section, i) => {
    if (i === 0) return { index: i, value: 100 }
    if (firstValue <= 0) return { index: i, value: 0 }
    return { index: i, value: (100 * (section.value || 0)) / firstValue }
  })
}

export type FunnelSection = {
  label: string
  subLabel?: string
  altLabel: string
  value?: number
  altValue?: number
  onDetailClick: () => void
  infoTooltip?: { title: string; message: string }
}

type FunnelChartProps = {
  width: number
  height: number
  sections: FunnelSection[]
  isLoading: boolean
  percentageView?: boolean
  colorScheme?: { base: string; segments: string[] }
  /** Chip recipe: one brand hue, tint stepping per stage with a hairline outline. Overrides colorScheme. */
  hue?: ChartHue
}

// Opacity steps on one shade rather than a 100->800 shade ramp: shades 700-950 lighten and
// 50-300 darken in dark mode (see dark-hues.css), which would reverse the deepening order.
const defaultColorScheme = {
  base: `text-blue-600/15`,
  segments: [`text-blue-600/35`, `text-blue-600/55`, `text-blue-600/75`, `text-blue-600`],
}

export function FunnelChart({
  width,
  height,
  sections,
  isLoading,
  percentageView = false,
  colorScheme = defaultColorScheme,
  hue,
}: FunnelChartProps) {
  const valuePadding = 5
  const [segments, setSegments] = useState<DataPoint[]>(mapDataToSections(sections))
  const [data, setData] = useState<DataPoint[]>(interpolateData(segments))
  const [numSegments, setNumSegments] = useState<number>(Math.max(...segments.map(x)))
  const [maxValue, setMaxValue] = useState(Math.max(...data.map(y)))
  const [minMax, setMinMax] = useState(maxValue + valuePadding)

  const hasAllSections = useMemo(() => sections.every((section) => typeof section.value !== `undefined`), [sections])
  const [hoveredStage, setHoveredStage] = useState<number | null>(null)

  const xScale = scaleLinear({
    range: [0, width],
    domain: [0, numSegments],
  })
  const yScale = scaleLinear({
    range: [height, 0],
    domain: [-minMax, minMax],
  })

  useEffect(() => {
    const segments = mapDataToSections(sections)
    const data = interpolateData(segments)
    const maxValue = Math.max(...data.map(y))
    setSegments(segments)
    setData(data)
    setNumSegments(Math.max(...segments.map(x)))
    setMaxValue(Math.max(...data.map(y)))
    setMinMax(maxValue + valuePadding)
  }, [sections])

  const areas = [
    { pad: 20, opacity: 1 },
    { pad: 5, opacity: 1 },
  ]

  const getPercentageChange = (prevVal: number | undefined, currVal: number | undefined): number => {
    if (!prevVal || !currVal || isNaN(prevVal) || isNaN(currVal) || prevVal <= 0 || currVal < 0) {
      return 0
    }
    return Math.round((currVal / prevVal) * 100)
  }

  return (
    <svg width={width} height={height + spaceForText + 15} className="flex justify-end align-bottom">
      <>
        {hasAllSections && (
          <>
            {/* <rect width={width} height={height} fill="#14134b" rx={22} /> */}
            {data?.map((d, i) => {
              if (!data[i + 1] || i === data.length - 1) return null
              const r = range(numSegments)
              return (
                <g
                  key={`label-${i}`}
                  id={`funnel-section-${i}`}
                  data-testid="funnel-section"
                  className="group"
                  onMouseEnter={() => setHoveredStage(d.index)}
                  onMouseLeave={() => setHoveredStage(null)}
                >
                  {r.includes(x(d)) && r.includes(d.index) && (
                    <>
                      <rect
                        x={xScale(x(d))}
                        y={0}
                        // Card hover grey, from state so hovering the ribbon greys the column too.
                        className={classNames(
                          `cursor-pointer rounded-t-3xl border-t fill-current group-hover:text-(--surface-muted)`,
                          hoveredStage === d.index ? `text-(--surface-muted)` : `text-(--surface)`,
                        )}
                        width={xScale(x(d) + 1) - xScale(x(d))}
                        height={height / 2 + spaceForText}
                        rx={8}
                        data-testid="funnel-section-bar"
                        onClick={() => sections.at(d.index)?.onDetailClick()}
                      />
                      <Group left={xScale(x(d)) + 20} top={20}>
                        <HtmlLabel showAnchorLine={false} verticalAnchor="start" horizontalAnchor="start">
                          <div className="flex w-max items-center gap-2 text-body-md text-default">
                            <div data-testid="funnel-section-label">
                              {!percentageView ? sections.at(d.index)?.label : sections.at(d.index)?.altLabel}
                            </div>
                            {!!sections.at(d.index)?.infoTooltip && (
                              <Info
                                title={sections.at(d.index)?.infoTooltip?.title || ``}
                                message={sections.at(d.index)?.infoTooltip?.message || ``}
                              />
                            )}
                          </div>
                          {!isLoading && (
                            <div className="mt-2 flex items-center">
                              <div
                                className="whitespace-nowrap text-heading-lg text-strong"
                                data-testid="funnel-section-count"
                              >
                                {!percentageView || d.index === 0
                                  ? sections.at(d.index)?.value?.toLocaleString(`en-US`)
                                  : `${getPercentageChange(
                                      sections.at(d.index - 1)?.value,
                                      sections.at(d.index)?.value,
                                    )}%`}
                              </div>
                              <div className="ml-2">
                                <TrendLabel
                                  previous={sections.at(d.index)?.altValue}
                                  current={sections.at(d.index)?.value}
                                />
                              </div>
                            </div>
                          )}
                          {!percentageView && (
                            <div className="mt-1 text-body-sm text-subtle">{sections.at(d.index)?.subLabel}</div>
                          )}
                        </HtmlLabel>
                      </Group>
                      <Group left={xScale(x(d) + 1) - 20} top={20}>
                        <HtmlLabel showAnchorLine={false} verticalAnchor="start" horizontalAnchor="end">
                          <OpenInWindow
                            height={24}
                            width={24}
                            className="hidden cursor-pointer text-default group-hover:block"
                          />
                        </HtmlLabel>
                      </Group>
                    </>
                  )}
                  {r.includes(x(d)) && (
                    <line
                      x1={xScale(x(d)) || 0.5}
                      x2={xScale(x(d)) || 0.5}
                      y1={20}
                      y2={height / 2 + spaceForText}
                      // stroke="black"
                      className="stroke-current text-(--surface-muted) "
                      // strokeOpacity={0.1}
                      strokeWidth={1}
                      shapeRendering={`crispEdges`}
                    />
                  )}
                  {i === 9 && (
                    <line
                      x1={xScale(x(d) + 1)}
                      x2={xScale(x(d) + 1)}
                      y1={20}
                      y2={height / 2 + spaceForText}
                      // stroke="black"
                      className="stroke-current text-(--surface-muted)"
                      // strokeOpacity={0.1}
                      strokeWidth={1}
                      shapeRendering={`crispEdges`}
                    />
                  )}
                </g>
              )
            })}
            {data?.map((d, i) => {
              if (!data[i + 1] || i === data.length - 1) return null
              const r = range(numSegments)
              return (
                <Fragment key={`area-${i}`}>
                  {r.includes(d.index) && (
                    <g
                      transform={`translate(0,${spaceForText})`}
                      className={hue ? `cursor-pointer` : undefined}
                      onMouseEnter={() => setHoveredStage(d.index)}
                      onMouseLeave={() => setHoveredStage(null)}
                      onClick={() => sections.at(d.index)?.onDetailClick()}
                    >
                      {areas?.map((area, i2) => {
                        const isHalo = i2 === 0
                        const stageAlpha = FUNNEL_STAGE_ALPHAS[Math.min(d.index, FUNNEL_STAGE_ALPHAS.length - 1)]
                        return (
                          <Area
                            key={`area-${i2}`}
                            data={data.slice(i, i + 4)}
                            curve={curveBasis}
                            x={(d2) => xScale(x(d2))}
                            y0={(d2) => yScale(y(d2) + area.pad)}
                            y1={(d2) => yScale(-y(d2) - area.pad)}
                            className={
                              hue
                                ? undefined
                                : classNames(`fill-current`, isHalo ? colorScheme.base : colorScheme.segments[d.index])
                            }
                            // `style`, not `fill`/`stroke` attributes: `hueTint` can return a
                            // `color-mix(..., var(--surface))` string, which only resolves
                            // reliably as CSS, not as an SVG presentation attribute. When `hue`
                            // is unset, `fill` stays out of `style` so the `fill-current`
                            // class above still drives the color.
                            style={{
                              fill: hue ? hueTint(hue, isHalo ? FUNNEL_HALO_ALPHA : stageAlpha) : undefined,
                              stroke: hue && !isHalo ? hueOutline(hue) : `transparent`,
                            }}
                            fillOpacity={isHalo ? 1 : area.opacity}
                            strokeWidth={1}
                          />
                        )
                      })}
                    </g>
                  )}
                </Fragment>
              )
            })}
          </>
        )}
      </>
    </svg>
  )
}
